import { useCallback, useEffect, useState } from "react";
import type { AdsLibraryAssistDraft } from "@/lib/adsAssistantClient";
import {
  listAdsCategories,
  listAdsPolicies,
  listAdsPostTagDefs,
  listAdsTemplates,
  type AdsAssistantCategoryRow,
  type AdsAssistantPolicyRow,
  type AdsAssistantPostTagDefRow,
  type AdsAssistantTemplateRow,
} from "@/lib/adsAssistantClient";
import type { LibrarySectionKey } from "./adsLibrarySectionShared";
import { useAdsAssistantLibraryTemplateEditor } from "./useAdsAssistantLibraryTemplateEditor";

export function useAdsAssistantLibrarySection(scopedOrgId: string | null) {
  const [categories, setCategories] = useState<AdsAssistantCategoryRow[]>([]);
  const [templates, setTemplates] = useState<AdsAssistantTemplateRow[]>([]);
  const [policies, setPolicies] = useState<AdsAssistantPolicyRow[]>([]);
  const [postTagDefs, setPostTagDefs] = useState<AdsAssistantPostTagDefRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const [catSlug, setCatSlug] = useState("");
  const [catLabel, setCatLabel] = useState("");
  const [polTitle, setPolTitle] = useState("");
  const [polBody, setPolBody] = useState("");
  const [polPatterns, setPolPatterns] = useState("");
  const [polSeverity, setPolSeverity] = useState<"info" | "warning" | "critical">("warning");
  const [postTagSlug, setPostTagSlug] = useState("");
  const [postTagLabel, setPostTagLabel] = useState("");
  const [postTagSortDraft, setPostTagSortDraft] = useState("0");
  const [postTagKind, setPostTagKind] = useState<"archetype" | "topic">("topic");
  const [expandedSection, setExpandedSection] = useState<LibrarySectionKey | null>(null);

  const load = useCallback(async () => {
    if (!scopedOrgId) return;
    setLoading(true);
    setError(null);
    const [c, t, p, tags] = await Promise.all([
      listAdsCategories(),
      listAdsTemplates({ includeSignoffProgress: true, includeArchetypeTags: true }),
      listAdsPolicies(),
      listAdsPostTagDefs(),
    ]);
    setLoading(false);
    if (!c.ok) return setError(c.error);
    if (!t.ok) return setError(t.error);
    if (!p.ok) return setError(p.error);
    if (!tags.ok) return setError(tags.error);
    setCategories(c.data.categories ?? []);
    setTemplates(t.data.templates ?? []);
    setPolicies(p.data.policies ?? []);
    setPostTagDefs(tags.data.tags ?? []);
  }, [scopedOrgId]);

  const editor = useAdsAssistantLibraryTemplateEditor({
    onChanged: load,
    setError,
    setBanner,
    onAfterOpen: () => setExpandedSection("templates"),
  });

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!banner) return;
    const id = window.setTimeout(() => setBanner(null), 2400);
    return () => window.clearTimeout(id);
  }, [banner]);

  const formHasContent = useCallback(
    () =>
      !!(
        catSlug.trim() ||
        catLabel.trim() ||
        editor.formHasContent() ||
        polTitle.trim() ||
        polBody.trim() ||
        polPatterns.trim() ||
        postTagSlug.trim() ||
        postTagLabel.trim()
      ),
    [catSlug, catLabel, editor, polTitle, polBody, polPatterns, postTagSlug, postTagLabel],
  );

  const applyAiDraft = useCallback(
    (d: AdsLibraryAssistDraft) => {
      let selected: LibrarySectionKey | null = null;
      if (d.template) {
        // Slice 19d/19e — preserve linked exemplars + custom texts while editing
        // an existing template; only clear them when applying a draft to a fresh
        // create form (the AI-rerun-inside-edit path keeps both intact).
        if (!editor.isEditing) {
          editor.setTplExamplePostIds([]);
          editor.setTplCustomExemplarTexts([]);
        }
        editor.setTplName(d.template.name);
        editor.setTplBody(d.template.body_markdown);
        editor.setTplCategoryId(d.template.category_id ?? "");
        editor.setTplArchetypeTagIds(d.template.archetype_tag_ids ?? []);
        editor.setTplSuggestedTopicTagIds(d.template.suggested_topic_tag_ids ?? []);
        selected = "templates";
      }
      if (d.policy) {
        setPolTitle(d.policy.title);
        setPolBody(d.policy.body_markdown);
        setPolPatterns(d.policy.patterns);
        setPolSeverity(d.policy.severity);
        selected = "policies";
      }
      if (d.category) {
        setCatSlug(d.category.slug);
        setCatLabel(d.category.label);
        selected = "categories";
      }
      if (!expandedSection && selected) setExpandedSection(selected);
    },
    [editor, expandedSection],
  );

  const applyBankTemplateDraft = useCallback(
    (d: AdsLibraryAssistDraft, selectedPostIds: string[]) => {
      if (!d.template) return;
      editor.setTplName(d.template.name);
      editor.setTplBody(d.template.body_markdown);
      editor.setTplCategoryId(d.template.category_id ?? "");
      // Bank flow always replaces exemplars with the user-picked set (create path).
      editor.setTplExamplePostIds(selectedPostIds);
      editor.setTplArchetypeTagIds(d.template.archetype_tag_ids ?? []);
      editor.setTplSuggestedTopicTagIds(d.template.suggested_topic_tag_ids ?? []);
      setExpandedSection("templates");
    },
    [editor],
  );

  const applyBankCategoryDraft = useCallback((d: AdsLibraryAssistDraft) => {
    if (!d.category) return;
    setCatSlug(d.category.slug);
    setCatLabel(d.category.label);
    setExpandedSection("categories");
  }, []);

  return {
    categories,
    templates,
    policies,
    postTagDefs,
    postTagSlug,
    setPostTagSlug,
    postTagLabel,
    setPostTagLabel,
    postTagSortDraft,
    setPostTagSortDraft,
    postTagKind,
    setPostTagKind,
    loading,
    error,
    setError,
    banner,
    setBanner,
    catSlug,
    setCatSlug,
    catLabel,
    setCatLabel,
    editor,
    polTitle,
    setPolTitle,
    polBody,
    setPolBody,
    polPatterns,
    setPolPatterns,
    polSeverity,
    setPolSeverity,
    expandedSection,
    setExpandedSection,
    load,
    formHasContent,
    applyAiDraft,
    applyBankTemplateDraft,
    applyBankCategoryDraft,
  };
}
